// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { getScrollableParents } from '../internal/utils/scrollable-containers';

const AUTO_SKELETON_VIEWPORT_BUFFER = 16;
const SKELETON_ROW_SELECTOR = 'tr[aria-hidden="true"]';

interface AutoSkeletonRowsCalculation {
  maxRows?: number;
  minRows: number;
  rowHeight: number;
  skeletonRowBottom: number;
  skeletonRowTop: number;
  tableBottom: number;
  viewportBottom: number;
}

interface AutoSkeletonRowReductionCalculation {
  currentRows: number;
  minRows: number;
  overflowHeight: number;
  rowHeight: number;
}

interface UseAutoSkeletonRowsProps {
  enabled: boolean;
  maxRows?: number;
  minRows?: number;
  tableBodyRef: React.RefObject<HTMLTableSectionElement>;
  tableRootRef: React.RefObject<HTMLElement>;
  tableWrapperRef: React.RefObject<HTMLElement>;
}

function getDocumentViewportBottom() {
  return document.documentElement.clientHeight || window.innerHeight;
}

function getViewportBottom(element: HTMLElement) {
  return getScrollableParents(element).reduce(
    (viewportBottom, scrollContainer) => Math.min(viewportBottom, scrollContainer.getBoundingClientRect().bottom),
    getDocumentViewportBottom()
  );
}

function getOverflowHeight(element: HTMLElement) {
  const scrollContainer = getScrollableParents(element)[0];
  if (scrollContainer) {
    return Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
  }

  return Math.max(
    0,
    Math.max(document.documentElement.scrollHeight, document.body.scrollHeight) - getDocumentViewportBottom()
  );
}

function calculateAutoSkeletonRows({
  maxRows,
  minRows,
  rowHeight,
  skeletonRowBottom,
  skeletonRowTop,
  tableBottom,
  viewportBottom,
}: AutoSkeletonRowsCalculation) {
  const staticHeightBelowSkeletonRow = Math.max(0, tableBottom - skeletonRowBottom);
  const availableHeight = Math.max(
    0,
    viewportBottom - skeletonRowTop - staticHeightBelowSkeletonRow - AUTO_SKELETON_VIEWPORT_BUFFER
  );
  const rows = Math.floor(availableHeight / rowHeight);
  return Math.max(minRows, Math.min(maxRows ?? Number.POSITIVE_INFINITY, rows));
}

function calculateAutoSkeletonRowReduction({
  currentRows,
  minRows,
  overflowHeight,
  rowHeight,
}: AutoSkeletonRowReductionCalculation) {
  return Math.min(
    currentRows - minRows,
    Math.max(1, Math.ceil((overflowHeight + AUTO_SKELETON_VIEWPORT_BUFFER) / rowHeight))
  );
}

export function useAutoSkeletonRows({
  enabled,
  maxRows,
  minRows,
  tableBodyRef,
  tableRootRef,
  tableWrapperRef,
}: UseAutoSkeletonRowsProps) {
  const maxRowsValue = maxRows === undefined ? undefined : Math.max(1, maxRows);
  const minRowsValue = Math.min(Math.max(1, minRows ?? 1), maxRowsValue ?? Number.POSITIVE_INFINITY);
  const [rows, setRows] = useState(minRowsValue);
  const rowHeightRef = useRef<number>();

  const updateRows = useCallback(() => {
    if (!enabled) {
      return;
    }

    const tableRoot = tableRootRef.current;
    const tableWrapper = tableWrapperRef.current;
    const skeletonRows = tableBodyRef.current?.querySelectorAll<HTMLElement>(SKELETON_ROW_SELECTOR);
    const firstSkeletonRowRect = skeletonRows?.[0]?.getBoundingClientRect();
    const lastSkeletonRowRect = skeletonRows?.[skeletonRows.length - 1]?.getBoundingClientRect();
    const rowHeight = lastSkeletonRowRect?.height ?? rowHeightRef.current;

    if (!tableRoot || !tableWrapper || !firstSkeletonRowRect || !lastSkeletonRowRect || !rowHeight) {
      return;
    }

    rowHeightRef.current = rowHeight;
    const nextRows = calculateAutoSkeletonRows({
      maxRows: maxRowsValue,
      minRows: minRowsValue,
      rowHeight,
      skeletonRowBottom: lastSkeletonRowRect.bottom,
      skeletonRowTop: firstSkeletonRowRect.top,
      tableBottom: tableRoot.getBoundingClientRect().bottom,
      viewportBottom: getViewportBottom(tableWrapper),
    });
    setRows(currentRows => (currentRows === nextRows ? currentRows : nextRows));
  }, [enabled, maxRowsValue, minRowsValue, tableBodyRef, tableRootRef, tableWrapperRef]);

  useLayoutEffect(updateRows, [updateRows]);

  useLayoutEffect(() => {
    const tableWrapper = tableWrapperRef.current;
    const rowHeight = rowHeightRef.current;
    if (!enabled || rows <= minRowsValue || !tableWrapper || !rowHeight) {
      return;
    }

    const overflowHeight = getOverflowHeight(tableWrapper);
    if (overflowHeight === 0) {
      return;
    }

    const rowsToRemove = calculateAutoSkeletonRowReduction({
      currentRows: rows,
      minRows: minRowsValue,
      overflowHeight,
      rowHeight,
    });
    setRows(currentRows => currentRows - rowsToRemove);
  }, [enabled, rows, minRowsValue, tableWrapperRef]);

  useLayoutEffect(() => {
    if (!enabled || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const tableRoot = tableRootRef.current;
    const tableWrapper = tableWrapperRef.current;
    if (!tableRoot || !tableWrapper) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(updateRows);
    resizeObserver.observe(tableRoot);
    resizeObserver.observe(tableWrapper);
    getScrollableParents(tableWrapper).forEach(scrollContainer => resizeObserver.observe(scrollContainer));
    window.addEventListener('resize', updateRows);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateRows);
    };
  }, [enabled, tableRootRef, tableWrapperRef, updateRows]);

  return rows;
}



// WEBPACK FOOTER //
// /Volumes/brazil-workspaces/Phoenix/PITLambda/src/table/use-auto-skeleton-rows.ts